import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PaymentsIcon from "@mui/icons-material/Payments";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import Helpers from "../Config/Helpers";
import { resolveLogoSrc } from "../utils/resolveLogoSrc";
import "./BulkPaymentHistory.css";

const money = (value) =>
  `$ ${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value, options = {}) => {
  const date = getLocalDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-US", options);
};

const getVehicleLabel = (vehicle = {}) =>
  [vehicle?.year, vehicle?.make || vehicle?.name, vehicle?.model]
    .filter(Boolean)
    .join(" ");

function BulkPaymentHistory() {
  const navigate = useNavigate();
  const { customer_name, customer_id, bulk_payment_id } = useParams();
  const isDetail = Boolean(bulk_payment_id);
  const [payments, setPayments] = useState([]);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../images/logo-light.png");
  const backToCustomer = customer_id
    ? `/user/receipts/customer/${customer_name}/${customer_id}`
    : "/user/reports";
  const backToHistory = customer_id
    ? `/user/receipts/customer/${customer_name}/${customer_id}/bulk-payments`
    : "/user/reports/bulk-payments";

  const loadPayments = useCallback(() => {
    setLoading(true);
    const query = customer_id
      ? `?customer_id=${encodeURIComponent(customer_id)}`
      : "";

    axios
      .get(`${Helpers.baseUrl}/receipts/bulk-payments${query}`, Helpers.headers)
      .then((response) => {
        setPayments(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoading(false));
  }, [customer_id, navigate]);

  const loadPayment = useCallback(() => {
    if (!bulk_payment_id) return;
    setLoading(true);

    axios
      .get(
        `${Helpers.baseUrl}/receipts/bulk-payments/${bulk_payment_id}`,
        Helpers.headers,
      )
      .then((response) => setPayment(response.data || null))
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoading(false));
  }, [bulk_payment_id, navigate]);

  useEffect(() => {
    if (isDetail) loadPayment();
    else loadPayments();
  }, [isDetail, loadPayment, loadPayments]);

  const filteredPayments = useMemo(() => {
    const term = search.trim().toLowerCase();
    const from = getLocalDate(fromDate);
    const to = getLocalDate(toDate);

    return payments.filter((item) => {
      const paymentDate = getLocalDate(item.paymentDate || item.createdAt);
      const text =
        [
          item.friendlyNumber,
          item.customer?.name,
          item.paymentType,
          item.note,
          item.totalAmount,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
      const textMatch = !term || text.includes(term);
      const dateMatch =
        (!from || (paymentDate && paymentDate >= from)) &&
        (!to || (paymentDate && paymentDate <= to));
      return textMatch && dateMatch;
    });
  }, [fromDate, payments, search, toDate]);

  const listSummary = useMemo(
    () =>
      filteredPayments.reduce(
        (acc, item) => {
          acc.count += 1;
          acc.invoices += Number(item.receiptCount) || 0;
          acc.amount += Number(item.totalAmount) || 0;
          return acc;
        },
        { count: 0, invoices: 0, amount: 0 },
      ),
    [filteredPayments],
  );

  const paymentRows = Array.isArray(payment?.receipts) ? payment.receipts : [];

  const historyColumns = [
    {
      field: "friendlyNumber",
      headerName: "Bulk #",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "paymentDate",
      headerName: "Payment Date",
      width: 150,
      renderCell: (params) =>
        formatDate(params.value, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
    },
    {
      field: "customer",
      headerName: "Customer",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => params.row.customer?.name || "-",
    },
    {
      field: "receiptCount",
      headerName: "Invoices",
      width: 110,
      renderCell: (params) => Number(params.value || 0),
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      width: 130,
      renderCell: (params) => money(params.value),
    },
    {
      field: "paymentType",
      headerName: "Method",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value || "Unknown"} size="small" />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          component={Link}
          to={
            customer_id
              ? `/user/receipts/customer/${customer_name}/${customer_id}/bulk-payments/${params.row._id}`
              : `/user/reports/bulk-payments/${params.row._id}`
          }
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon fontSize="small" />}
          sx={{ textTransform: "none" }}
        >
          View
        </Button>
      ),
    },
  ];

  const receiptColumns = [
    {
      field: "receiptNumber",
      headerName: "Invoice",
      width: 120,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "date",
      headerName: "Invoice Date",
      width: 150,
      renderCell: (params) =>
        formatDate(params.value, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => getVehicleLabel(params.value) || "-",
    },
    {
      field: "totalPrice",
      headerName: "Total",
      width: 120,
      renderCell: (params) => money(params.value),
    },
    {
      field: "previousPaid",
      headerName: "Paid Before",
      width: 130,
      renderCell: (params) => money(params.value),
    },
    {
      field: "amountPaid",
      headerName: "Paid Now",
      width: 120,
      renderCell: (params) => money(params.value),
    },
    {
      field: "remainingAfter",
      headerName: "Balance",
      width: 120,
      renderCell: (params) => money(params.value),
    },
  ];

  const renderHeader = (title, description, actions = null) => (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 3,
        background: "linear-gradient(135deg, #2563eb 0%, #0f766e 100%)",
        borderRadius: 2,
        color: "white",
      }}
    >
      <Breadcrumbs
        separator={
          <NavigateNextIcon
            fontSize="small"
            sx={{ color: "rgba(255,255,255,0.7)" }}
          />
        }
        sx={{ mb: 2 }}
      >
        <MuiLink
          component={Link}
          to="/user/dashboard"
          underline="hover"
          sx={{ color: "rgba(255,255,255,0.9)", display: "flex" }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Home
        </MuiLink>
        <MuiLink
          component={Link}
          to={customer_id ? backToCustomer : "/user/reports"}
          underline="hover"
          sx={{ color: "rgba(255,255,255,0.9)" }}
        >
          {customer_id ? "Customer Receipts" : "Reports"}
        </MuiLink>
        <Typography sx={{ color: "white", fontWeight: 600 }}>{title}</Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <PaymentsIcon sx={{ fontSize: 38 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.88)" }}>
              {description}
            </Typography>
          </Box>
        </Stack>
        {actions}
      </Stack>
    </Paper>
  );

  if (isDetail) {
    const title = payment?.friendlyNumber
      ? `Bulk Payment ${payment.friendlyNumber}`
      : "Bulk Payment";

    return (
      <>
        <Box
          className="bulk-payment-screen"
          sx={{ pt: { xs: 10, sm: 11, md: 12 }, pb: 3, minHeight: "100vh", backgroundColor: "#f5f7fa" }}
        >
          <Container maxWidth={false} sx={{ maxWidth: "100% !important", px: { xs: 2, sm: 3, md: 4 } }}>
            {renderHeader(
              title,
              "Review and reprint this bulk payment receipt.",
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  component={Link}
                  to={backToHistory}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.75)",
                    textTransform: "none",
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={() => window.print()}
                  variant="contained"
                  startIcon={<PrintIcon />}
                  sx={{ textTransform: "none", backgroundColor: "#111827" }}
                  disabled={!payment}
                >
                  Print
                </Button>
              </Stack>,
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                ["Customer", payment?.customer?.name || "-"],
                ["Payment Date", formatDate(payment?.paymentDate, { month: "long", day: "numeric", year: "numeric" })],
                ["Payment Type", payment?.paymentType || "-"],
                ["Total Paid", money(payment?.totalAmount)],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} lg={3} key={label}>
                  <Card elevation={0} sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography sx={{ color: "#667085", mb: 0.5 }}>
                        {label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {payment?.note ? (
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                <Typography sx={{ color: "#667085", mb: 0.5 }}>Notes</Typography>
                <Typography>{payment.note}</Typography>
              </Paper>
            ) : null}

            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Paid Invoices
              </Typography>
              <ReusableDataGrid
                rows={paymentRows.map((row) => ({ ...row, id: row.receipt_id }))}
                columns={receiptColumns}
                loading={loading}
                noRowsMessage="No invoices found for this bulk payment"
                disableInternalScroll
              />
            </Paper>
          </Container>
        </Box>

        {payment ? (
          <div className="bulk-payment-print">
            <div className="print-header">
              <div className="print-company">
                {shopLogoSrc ? (
                  <img src={shopLogoSrc} alt="Company Logo" className="print-company-logo" />
                ) : (
                  <div className="print-company-name">
                    {Helpers.authShop?.name || "Shop"}
                  </div>
                )}
                <div className="print-company-contact">
                  <div style={{ whiteSpace: "pre-line" }}>
                    {Helpers.authShop?.address?.replace(", ", ",\n") || ""}
                  </div>
                  <div>{Helpers.authShop?.phone || ""}</div>
                </div>
              </div>
              <div className="print-meta" style={{ textAlign: "right" }}>
                <div className="print-title">Bulk Payment Receipt</div>
                <div>Bulk #: {payment.friendlyNumber}</div>
                <div>
                  Payment Date:{" "}
                  {formatDate(payment.paymentDate, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <div>Customer: {payment.customer?.name || "-"}</div>
                <div>Method: {payment.paymentType || "-"}</div>
              </div>
            </div>

            <table>
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Vehicle</th>
                  <th className="money">Total</th>
                  <th className="money">Paid Before</th>
                  <th className="money">Paid Now</th>
                  <th className="money">Balance</th>
                </tr>
              </thead>
              <tbody>
                {paymentRows.map((row) => (
                  <tr key={row.receipt_id}>
                    <td>{row.receiptNumber || "-"}</td>
                    <td>{formatDate(row.date, { month: "short", day: "numeric", year: "numeric" })}</td>
                    <td>{getVehicleLabel(row.vehicle) || "-"}</td>
                    <td className="money">{money(row.totalPrice)}</td>
                    <td className="money">{money(row.previousPaid)}</td>
                    <td className="money">{money(row.amountPaid)}</td>
                    <td className="money">{money(row.remainingAfter)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5">Totals</td>
                  <td className="money">{money(payment.totalAmount)}</td>
                  <td className="money">{money(0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: "100% !important", px: { xs: 2, sm: 3, md: 4 } }}>
        {renderHeader(
          "Bulk Payment History",
          customer_name
            ? `Bulk payments recorded for ${customer_name}.`
            : "Bulk payments recorded across customers.",
          <Button
            component={Link}
            to={backToCustomer}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "white",
              borderColor: "rgba(255,255,255,0.75)",
              textTransform: "none",
            }}
          >
            Back
          </Button>,
        )}

        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={loadPayments}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Refresh
            </Button>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ["Bulk Payments", listSummary.count, <PaymentsIcon />],
            ["Invoices Paid", listSummary.invoices, <ReceiptLongIcon />],
            ["Total Collected", money(listSummary.amount), <PaymentsIcon />],
          ].map(([label, value, icon]) => (
            <Grid item xs={12} sm={6} lg={4} key={label}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography sx={{ color: "#667085", mb: 0.5 }}>
                        {label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {value}
                      </Typography>
                    </Box>
                    <Box sx={{ color: "#2563eb" }}>{icon}</Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            History
          </Typography>
          <ReusableDataGrid
            rows={filteredPayments.map((row) => ({ ...row, id: row._id }))}
            columns={historyColumns}
            loading={loading}
            pageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
            noRowsMessage="No bulk payments found"
            disableInternalScroll
          />
        </Paper>
      </Container>
    </Box>
  );
}

export default BulkPaymentHistory;
